import { useDroppable } from "@dnd-kit/core";
import type { Lead, LeadStage } from "@/mocks/leadsData";
import { DealCard } from "./DealCard";

type Props = {
  stage: LeadStage | string;
  label: string;
  deals: Lead[];
  colorBar: string;
  isDragOver: boolean;
  isDealDragging: boolean;
  onAddDeal?: (stage: string) => void;
};

export function KanbanColumn({ stage, label, deals, colorBar, isDragOver, isDealDragging, onAddDeal }: Props) {
  const { setNodeRef, isOver } = useDroppable({
    id: stage,
    data: { type: "column", stage },
  });

  const totalValue = deals.reduce((sum, d) => {
    const num = parseFloat((d.value || "0").replace(/[₹,]/g, ""));
    return sum + (isNaN(num) ? 0 : num);
  }, 0);

  const formatValue = (val: number) => {
    if (val >= 10000000) return `₹${(val / 10000000).toFixed(1)}Cr`;
    if (val >= 100000) return `₹${(val / 100000).toFixed(1)}L`;
    if (val >= 1000) return `₹${(val / 1000).toFixed(1)}K`;
    return `₹${val.toLocaleString()}`;
  };

  return (
    <div
      ref={setNodeRef}
      className={`flex-shrink-0 w-[290px] flex flex-col transition-colors duration-200 rounded-xl ${
        isDragOver || isOver ? "bg-indigo-50/50" : ""
      }`}
    >
      {/* Column header */}
      <div className={`w-full h-1 rounded-t-lg ${colorBar} mb-3 transition-opacity duration-200 ${isDragOver || isOver ? "opacity-100" : "opacity-60"}`} />
      <div
        className={`flex items-center gap-2 mb-2 px-1 pb-1 transition-all duration-200 ${
          isDragOver ? "border-b-2 border-indigo-400" : "border-b-2 border-transparent"
        }`}
      >
        <h3 className="font-semibold text-gray-900 text-sm truncate">{label}</h3>
        <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-600 flex-shrink-0">
          {deals.length}
        </span>
        {deals.length > 0 && (
          <span className="text-xs text-gray-500 ml-auto whitespace-nowrap font-medium">{formatValue(totalValue)}</span>
        )}
      </div>

      {/* Cards container */}
      <div className="flex-1 overflow-y-auto pr-1 min-h-[220px]">
        {deals.length > 0 ? (
          deals.map((deal) => <DealCard key={deal.id} deal={{ ...deal, stage: stage as LeadStage }} />)
        ) : (
          <div className={`border-2 border-dashed rounded-xl flex flex-col items-center justify-center py-10 px-4 text-center mt-2 transition-colors duration-200 ${
            isDragOver || isOver ? "border-indigo-300 bg-indigo-50/80" : "border-gray-200"
          }`}>
            <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 transition-colors duration-200 ${
              isDragOver || isOver ? "bg-indigo-100" : "bg-gray-100"
            }`}>
              {isDragOver || isOver ? (
                <i className="ri-inbox-unarchive-line text-2xl text-indigo-400" />
              ) : (
                <i className="ri-inbox-line text-2xl text-gray-300" />
              )}
            </div>
            {isDragOver || isOver ? (
              <p className="text-sm text-indigo-500 font-medium">Drop here</p>
            ) : (
              <>
                <p className="text-xs text-gray-400 mb-2">No deals in this stage</p>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onAddDeal?.(stage); }}
                  className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 font-medium whitespace-nowrap transition-colors cursor-pointer hover:underline"
                >
                  <i className="ri-add-line text-sm" /> Add Deal
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* "+ Add Deal" at bottom of column */}
      {deals.length > 0 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onAddDeal?.(stage); }}
          className="mt-2 border-2 border-dashed border-gray-200 rounded-xl p-2.5 text-center cursor-pointer hover:bg-indigo-50 hover:border-indigo-300 transition-all duration-200 group"
        >
          <div className="flex items-center justify-center gap-1.5">
            <i className="ri-add-line text-sm text-indigo-400 group-hover:text-indigo-600 transition-colors" />
            <span className="text-xs text-indigo-600 font-medium whitespace-nowrap">Add Deal</span>
          </div>
        </button>
      )}
    </div>
  );
}